import { Ban, PlayCircle, Trash2, Undo2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ErrorState, Skeleton, useToast } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatDate } from '@/lib/utils';

import { useBusinessAction, useBusinessUsers, type BusinessAction } from './admin-actions-api';
import { ConfirmActionDialog } from './confirm-action-dialog';
import { AddUserDialog, UserList } from './user-list';

interface Business {
  id: string;
  name: string;
  isActive: boolean;
  suspendedAt: string | null;
  deletionScheduledFor: string | null;
}

/**
 * פעולות התמיכה במגירת העסק: השעיה / הפעלה, מחיקה בשני שלבים (סימון + 14 יום,
 * ניתן לביטול), ומשתמשי העסק. כל פעולה הרסנית — דרך חלון אישור; מחיקה — עם
 * הקלדת שם העסק.
 */
export function BusinessActions({ business }: { business: Business }) {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const action = useBusinessAction();
  const users = useBusinessUsers(business.id);
  const [confirm, setConfirm] = useState<BusinessAction | null>(null);
  const [adding, setAdding] = useState(false);

  const run = (kind: BusinessAction, confirmName?: string) =>
    action.mutate(
      { id: business.id, action: kind, confirmName },
      {
        onSuccess: () => {
          setConfirm(null);
          toast.success(t(`businessActions.done.${kind}`, { name: business.name }));
        },
        onError: (err) =>
          toast.error(
            t('businessActions.failed'),
            err instanceof ApiError ? err.message : undefined,
          ),
      },
    );

  const pending = business.deletionScheduledFor !== null;

  return (
    <section className="space-y-4">
      {pending ? (
        <p
          role="status"
          className="rounded-(--radius-md) border border-danger-border bg-danger-subtle px-3 py-2 text-xs text-danger"
        >
          {t('businessActions.pendingDeletion', {
            date: formatDate(business.deletionScheduledFor),
          })}
        </p>
      ) : !business.isActive ? (
        <p
          role="status"
          className="rounded-(--radius-md) border border-warning-border bg-warning-subtle px-3 py-2 text-xs text-warning"
        >
          {business.suspendedAt
            ? t('businessActions.suspendedSince', { date: formatDate(business.suspendedAt) })
            : t('businesses.inactive')}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {pending ? (
          <Button size="sm" variant="secondary" onClick={() => setConfirm('cancel-deletion')}>
            <Undo2 aria-hidden />
            {t('businessActions.cancel-deletion')}
          </Button>
        ) : business.isActive ? (
          <Button size="sm" variant="secondary" onClick={() => setConfirm('suspend')}>
            <Ban aria-hidden />
            {t('businessActions.suspend')}
          </Button>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setConfirm('reactivate')}>
            <PlayCircle aria-hidden />
            {t('businessActions.reactivate')}
          </Button>
        )}
        {!pending ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
              <UserPlus aria-hidden />
              {t('users.add')}
            </Button>
            <Button size="sm" variant="danger" onClick={() => setConfirm('schedule-deletion')}>
              <Trash2 aria-hidden />
              {t('businessActions.schedule-deletion')}
            </Button>
          </>
        ) : null}
      </div>

      <div>
        <h3 className="mb-1 text-xs font-semibold text-fg">{t('users.title')}</h3>
        {users.isError ? (
          <ErrorState error={users.error} onRetry={() => void users.refetch()} />
        ) : !users.data ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <UserList users={users.data.rows} />
        )}
      </div>

      {confirm ? (
        <ConfirmActionDialog
          title={t(`businessActions.confirm.${confirm}.title`, { name: business.name })}
          description={t(`businessActions.confirm.${confirm}.description`)}
          confirmLabel={t(`businessActions.${confirm}`)}
          danger={confirm === 'suspend' || confirm === 'schedule-deletion'}
          typeToConfirm={confirm === 'schedule-deletion' ? business.name : undefined}
          loading={action.isPending}
          onClose={() => setConfirm(null)}
          onConfirm={(typed) => run(confirm, confirm === 'schedule-deletion' ? typed : undefined)}
        />
      ) : null}
      {adding ? <AddUserDialog businessId={business.id} onClose={() => setAdding(false)} /> : null}
    </section>
  );
}
